/**
 * Build steps in plain words, for site owners rather than developers (ported
 * from Dunea Work's plainSteps). No tool names, no paths, no commands on the
 * row: what Dunea is doing, and at most the one thing it is doing it to. The
 * technical detail survives only in the tooltip, for whoever wants it.
 *
 * Pure module — no React — so the wording is unit-testable.
 */
import type { ToolPart } from '~/components/chat/message-parts';
import { toolNameOf } from './tool-meta';

export interface PlainStep {
  icon: string;
  /** "Created the home page" (done) / "Creating the home page" (in progress). */
  text: string;
  /** One sentence for the tooltip: what this step is for. */
  explain: string;
  /** The raw thing it acted on (path, command) — tooltip only. */
  tech?: string;
}

const str = (input: Record<string, any> | undefined, key: string): string | undefined =>
  input && typeof input[key] === 'string' ? (input[key] as string) : undefined;

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** `pages/shop/[slug].tsx` → "shop/[slug]" words → "Shop detail". */
function pageName(route: string): string {
  const words = route
    .split('/')
    .filter((seg) => seg && seg !== 'index')
    .map((seg) => (/^\[.+\]$/.test(seg) ? 'detail' : seg.replace(/[-_]+/g, ' ')));

  return words.length === 0 || (words.length === 1 && words[0] === 'home')
    ? 'the home page'
    : `the ${capitalize(words.join(' '))} page`;
}

/**
 * What a project file IS to a site owner. Pages are named by their route, the
 * shell and styles by their job; anything else falls back to its file name.
 */
export function plainFileName(path: string | undefined): string {
  if (!path) {
    return 'a file';
  }

  const p = path.replace(/\\/g, '/');
  const base = p.split('/').pop() ?? p;
  const page = p.match(/(?:^|\/)pages\/(.+)\.(?:tsx|jsx|ts|js)$/);

  if (page) {
    if (/(?:^|\/)_layout$/.test(page[1])) {
      return 'the site layout';
    }

    if (/(?:^|\/)404$|notfound$/i.test(page[1])) {
      return 'the not-found page';
    }

    return pageName(page[1]);
  }

  if (/(?:^|\/)(sections|components)\//.test(p) && !/\/components\/ui\//.test(p)) {
    const name = base
      .replace(/\.[^.]+$/, '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]+/g, ' ');

    return `the ${name.toLowerCase()} section`;
  }

  if (/\.css$/.test(base) || /^tailwind\.config\./.test(base)) {
    return 'the site styles';
  }

  if (base === 'index.html') {
    return 'the page title and sharing info';
  }

  if (/(?:^|\/)worker\//.test(p)) {
    return /schema/.test(base) ? 'the database' : 'the backend';
  }

  return base;
}

/** What a shell command is doing, in words — never the command itself. */
function describeCommand(command: string | undefined, done: boolean): string {
  const c = (command ?? '').trim();

  if (/\b(pnpm|npm|yarn|bun)\s+(add|install|i)\b/.test(c)) {
    return done ? 'Installed what the site needs' : 'Installing what the site needs';
  }

  if (/\b(tsc|lint|eslint|typecheck)\b/.test(c)) {
    return done ? 'Checked the code for mistakes' : 'Checking the code for mistakes';
  }

  if (/\b(build|vite build)\b/.test(c)) {
    return done ? 'Built the site' : 'Building the site';
  }

  return done ? 'Ran a setup step' : 'Running a setup step';
}

/**
 * The file tools report success with a fixed opening ("Edited …", "File
 * written: …"); anything else they return — a guard's [BLOCKED], a rolled-back
 * edit, "not read yet" — means nothing was written.
 */
const FILE_TOOL_SUCCESS: Record<string, RegExp> = {
  write_file: /^File written: /,
  edit_file: /^Edited /,
  apply_diff: /^Applied diff to /,
  insert_at_line: /^Inserted \d+ line/,
};

/**
 * False when a finished file write/edit did not change the file (it errored,
 * or a guard refused it). True for every other step, and for steps still running.
 */
export function changeLanded(part: ToolPart): boolean {
  const success = FILE_TOOL_SUCCESS[toolNameOf(part)];

  if (!success) {
    return true;
  }

  if (part.state === 'output-error') {
    return false;
  }

  if (part.state !== 'output-available') {
    return true;
  }

  const out = part.output;

  return typeof out === 'string' ? success.test(out.trimStart()) : true;
}

export function plainStep(part: ToolPart, done: boolean): PlainStep {
  const name = toolNameOf(part);
  const input = part.input;
  const path = str(input, 'filePath') ?? (Array.isArray(input?.filePaths) ? input!.filePaths[0] : undefined);
  const did = (doing: string, past: string) => (done ? past : doing);

  // The agent tried a change that a check refused or that didn't match. That
  // is the agent's own step, not an error for the owner: say what it tried.
  if (done && !changeLanded(part)) {
    return {
      icon: 'i-ph:arrow-u-up-left',
      text: path ? `Tried to update ${plainFileName(path)}` : 'Tried a change',
      explain: 'Dunea tried this change and left the file as it was. Click for the details.',
      tech: path,
    };
  }

  switch (name) {
    case 'write_file':
      return {
        icon: 'i-ph:file-plus',
        text: path
          ? `${did('Creating', 'Created')} ${plainFileName(path)}`
          : did('Writing the next part', 'Wrote a new part'),
        explain: 'Wrote a new part of your site.',
        tech: path,
      };
    case 'bulk_write_files': {
      const n = Array.isArray(input?.files) ? input!.files.length : 0;

      return {
        icon: 'i-ph:files',
        text: n > 1 ? `${did('Creating', 'Created')} ${n} files` : `${did('Creating', 'Created')} files`,
        explain: 'Wrote several parts of your site at once.',
      };
    }
    case 'edit_file':
    case 'apply_diff':
    case 'insert_at_line':
      return {
        icon: 'i-ph:pencil-simple',
        text: path ? `${did('Updating', 'Updated')} ${plainFileName(path)}` : did('Making a change', 'Made a change'),
        explain: 'Changed part of your site.',
        tech: path,
      };
    case 'read_files': {
      const paths: string[] = Array.isArray(input?.filePaths) ? input!.filePaths : path ? [path] : [];

      return {
        icon: 'i-ph:file-text',
        text: did('Looking at your project', 'Looked at your project'),
        explain: 'Read the existing files to understand what is already there.',
        tech: paths.join(', ') || undefined,
      };
    }
    case 'list_files':
    case 'glob':
    case 'search_code':
      return {
        icon: 'i-ph:magnifying-glass',
        text: did('Looking through your project', 'Looked through your project'),
        explain: 'Searched the project to find the right place to work.',
        tech: str(input, 'pattern') ?? str(input, 'query') ?? str(input, 'directory') ?? str(input, 'path'),
      };
    case 'run_shell':
      return {
        icon: 'i-ph:gear-six',
        text: describeCommand(str(input, 'command'), done),
        explain: 'Ran a behind-the-scenes step to set up or check the site.',
        tech: str(input, 'command'),
      };
    case 'start_server':
      return {
        icon: 'i-ph:play',
        text: did('Starting the live preview', 'Started the live preview'),
        explain: 'Started your site so you can see it in the preview.',
      };
    case 'lint_check':
      return {
        icon: 'i-ph:check-circle',
        text: did('Checking the code for mistakes', 'Checked the code for mistakes'),
        explain: 'An automatic check that catches typos and errors before you see them.',
      };
    case 'take_screenshot':
      return {
        icon: 'i-ph:camera',
        text: did('Looking at the page', 'Looked at the page'),
        explain: 'Took a picture of the page to check how it looks.',
      };
    case 'review_ui':
      return {
        icon: 'i-ph:eye',
        text: did('Reviewing the design', 'Reviewed the design'),
        explain: 'A second look at every page, on desktop and phone, to catch anything that looks off.',
      };
    case 'review_code':
      return {
        icon: 'i-ph:code',
        text: did('Reviewing the code', 'Reviewed the code'),
        explain: 'A second look at the code to catch problems.',
      };
    case 'test_backend':
      return {
        icon: 'i-ph:plugs-connected',
        text: did('Testing the backend', 'Tested the backend'),
        explain: 'Checked that forms and saved data work.',
      };
    case 'research':
      return {
        icon: 'i-ph:book-open-text',
        text: did('Researching the topic', 'Researched the topic'),
        explain: 'Looked up facts and examples for your site.',
      };
    case 'db_migrate':
      return {
        icon: 'i-ph:database',
        text: did('Updating the database', 'Updated the database'),
        explain: 'Set up where your site stores its data.',
      };
    case 'task_manager':
      return {
        icon: 'i-ph:list-checks',
        text: did('Planning the steps', 'Planned the steps'),
        explain: 'Broke the work into steps — you can follow them in the progress card.',
      };
    case 'component_library':
      return {
        icon: 'i-ph:package',
        text: did('Picking ready-made pieces', 'Picked ready-made pieces'),
        explain: 'Chose polished, ready-made building blocks for the page.',
      };
    case 'http_request':
      return {
        icon: 'i-ph:globe',
        text: did('Checking a web address', 'Checked a web address'),
        explain: 'Visited a web address to check it works.',
        tech: str(input, 'url'),
      };
    case 'remember':
      return {
        icon: 'i-ph:brain',
        text: did('Saving a note for next time', 'Saved a note for next time'),
        explain: 'Remembered a preference so future changes match it.',
      };
    case 'typography_presets':
      return {
        icon: 'i-ph:text-aa',
        text: did('Choosing the fonts', 'Chose the fonts'),
        explain: 'Picked a font pairing that suits your brand.',
      };
    case 'motion_treatment':
      return {
        icon: 'i-ph:sparkle',
        text: did('Designing the animation', 'Designed the animation'),
        explain: 'Planned the signature motion moment for the page.',
      };
    case 'product_mockup':
      return {
        icon: 'i-ph:browser',
        text: did('Making a product preview', 'Made a product preview'),
        explain: 'Drew a mockup of your product to show on the page.',
      };
    case 'get_user_uploads':
      return {
        icon: 'i-ph:paperclip',
        text: did('Checking your uploads', 'Checked your uploads'),
        explain: 'Looked at the images or files you attached.',
      };
    case 'ask_user':
      return { icon: 'i-ph:question', text: 'Asked you a question', explain: 'Needed your input to continue.' };
    default:
      if (/image|photo|pexels|video|media/.test(name)) {
        return {
          icon: 'i-ph:image',
          text: did('Finding images', 'Found images'),
          explain: 'Looked for photos that fit your site.',
          tech: str(input, 'query'),
        };
      }

      return {
        icon: 'i-ph:sparkle',
        text: did('Working on your site', 'Worked on your site'),
        explain: 'A step in building your site.',
      };
  }
}

const FILE_CREATE = new Set(['write_file', 'bulk_write_files']);
const FILE_EDIT = new Set(['edit_file', 'apply_diff', 'insert_at_line']);
const CHECKS = new Set(['lint_check', 'review_ui', 'review_code', 'test_backend', 'take_screenshot']);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "Created 6 pages · Made 9 edits · Ran 4 checks" — the settled line for a run of steps. */
export function plainSummary(parts: ToolPart[]): string {
  const created = new Set<string>();
  let createdBulk = 0;
  let edits = 0;
  let checks = 0;
  let notApplied = 0;
  let other = 0;

  for (const part of parts) {
    const name = toolNameOf(part);

    if (!changeLanded(part)) {
      notApplied++;
    } else if (FILE_CREATE.has(name)) {
      const path = str(part.input, 'filePath');

      if (path) {
        created.add(path);
      } else {
        createdBulk += Array.isArray(part.input?.files) ? part.input!.files.length : 1;
      }
    } else if (FILE_EDIT.has(name)) {
      edits++;
    } else if (CHECKS.has(name)) {
      checks++;
    } else {
      other++;
    }
  }

  const pages = [...created].filter((p) => /(?:^|\/)pages\/(?!_)/.test(p)).length;
  const otherFiles = created.size - pages + createdBulk;
  const bits: string[] = [];

  if (pages > 0) {
    bits.push(`Created ${plural(pages, 'page', 'pages')}`);
  }

  if (otherFiles > 0) {
    bits.push(`Wrote ${plural(otherFiles, 'file', 'files')}`);
  }

  if (edits > 0) {
    bits.push(`Made ${plural(edits, 'edit', 'edits')}`);
  }

  if (checks > 0) {
    bits.push(`Ran ${plural(checks, 'check', 'checks')}`);
  }

  if (notApplied > 0 && bits.length === 0) {
    return `Tried ${plural(notApplied, 'change', 'changes')}`;
  }

  if (bits.length === 0) {
    return other > 0 ? plural(other, 'step', 'steps') : '';
  }

  return bits.join(' · ');
}
